import {useEffect, useState, useMemo } from 'react';
import "../../../styles/DashboardStyles/DashboardContent/DashboardQuotes.css"
import tickIcon from "../../../assets/images/icons/check_circle_100dp_FFFF_FILL0_wght400_GRAD0_opsz48.png";
import requestQuoteIcon from "../../../assets/images/icons/request_quote_60dp_FFFF_FILL0_wght400_GRAD0_opsz48.png";
import rejectIcon from "../../../assets/images/icons/cancel_100dp_FFFF_FILL0_wght400_GRAD0_opsz48.png";
import { db } from '../../../firebase/config';
import { collection, onSnapshot, doc, orderBy, query, updateDoc } from "firebase/firestore";

// ── TYPES ──────────────────────────────────────────────────────────────────
type Status = "pending" | "accepted" | "sent" | "rejected";

interface Quote {
  id: string;
  userId: string;
  fullName: string;
  phoneNum: string;
  email: string;
  compName: string;
  licenseTypes: string[];
  licenseType: string;
  projectDetails: string;
  documentFiles: { name: string; url: string }[];
  createdAt: Date;
  status: Status;
}

// ── STATUS CONFIG (label + colour in one place) ────────────────────────────
const STATUS_CONFIG: Record<Status, { label: string; color: string }> = {
  pending:  { label: "Pending",    color: "#FFC107" },
  accepted: { label: "Accepted",   color: "#28A745" },
  sent:     { label: "Sent Quote", color: "#007BFF" },
  rejected: { label: "Rejected",   color: "#DC3545" },
};

const SoftDeleteQuote = async (id: string) => {
  try {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    // Store the expiry date on the document
    const quoteRef = doc(db, "quotes", id);
    await updateDoc(quoteRef, {
      deleteAt: thirtyDaysFromNow
    });

    console.log(`Quote ${id} scheduled for deletion on ${thirtyDaysFromNow.toDateString()}`);

  } catch (err) {
    console.error("Failed to schedule deletion:", err);
  }
};

export default function DashboardQuotes() {
  const [quotes, setQuotes]       = useState<Quote[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [search, setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState<Status | "all">("all");
  const [selectedId, setSelectedId]     = useState<string | null>(null);




useEffect(() => {

  // Build the query — newest quotes first
  const q = query(
    collection(db, "quotes"),
    orderBy("createdAt", "desc")
  );

  // onSnapshot listens in real time — updates automatically
  // when a quote is added, changed or deleted in Firestore
  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const data: Quote[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),

        // Firestore stores dates as Timestamps — convert to JS Date
        createdAt: doc.data().createdAt?.toDate() ?? new Date(),
      })) as Quote[];

      setQuotes(data);
      setLoading(false);
    },
    (err) => {
      console.error("Error fetching quotes:", err);
      setError("Failed to load quotes. Please try again.");
      setLoading(false);
    }
  );

  // Cleanup — stops listening when the component unmounts
  return () => unsubscribe();

}, []); // empty array = runs once when component mounts

  // ── DERIVED: filtered + sorted list ──────────────────────────────────────
  const filteredQuotes = useMemo(() => {
    return quotes.filter(q => {

      // 1. Search — matches name OR email (case-insensitive)
      const term = search.toLowerCase();
      const matchesSearch =
        q.fullName.toLowerCase().includes(term) ||
        q.email.toLowerCase().includes(term);

      // 2. Status dropdown — "all" shows everything
      const matchesStatus =
        statusFilter === "all" || q.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [quotes, search, statusFilter]);

  // ── DERIVED: currently selected quote ─────────────────────────────────────
  const selectedQuote = quotes.find(q => q.id === selectedId) ?? null;

const updateStatus = async (id: string, newStatus: Status) => {
  try {
    // 1. Update in Firestore
    const quoteRef = doc(db, "quotes", id);
    await updateDoc(quoteRef, {
      status: newStatus,
      updatedAt: new Date()  // good practice to track when it changed
    });

    // 2. Update local state to reflect change instantly (no need to wait for onSnapshot)
    setQuotes(prev =>
      prev.map(q => q.id === id ? { ...q, status: newStatus } : q)
    );

    if (newStatus === "sent") {
      const subject = encodeURIComponent(
        `Quotation for ${selectedQuote?.compName}`
      );

      const body = encodeURIComponent(
        `Dear ${selectedQuote?.fullName},

        Please find your quotation attached.

        If you have any questions, feel free to contact us.

        Kind regards,
        Kole Medical Team`
      );

      window.location.href = `mailto:${selectedQuote?.email}?subject=${subject}&body=${body}`;
    }

    if(newStatus === "rejected"){
      alert("You have rejected this quotation. You have 30 days till the quote expires.");

      SoftDeleteQuote(id);/*The Quote will be deleted after 30 days*/
    } else {
      // Any other status — clear the deleteAt so deletion is cancelled
      await updateDoc(quoteRef, {
        status: newStatus,
        updatedAt: new Date(),
        deleteAt: null                // ✅ cancel scheduled deletion
      });
    }

  } catch (err) {
    console.error("Failed to update status:", err);
    setError("Failed to update status. Please try again.");
  }
};

  return (
    <div className="dashboardQuotes">
      <h1>Dashboard Quotes</h1>

      <div className="main-container">

        {/* ── LEFT: user list ── */}
        <div className="user-container">

          {/* Search input */}
          <input
            type="text"
            placeholder="Search Name / Email"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />

          {/* Status dropdown */}
          <div className="dropdown-sort">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as Status | "all")}
            >
              <option value="all">All</option>
              <option value="accepted">Accepted</option>
              <option value="sent">Sent Quote</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* User list */}
          <ul className="users-list">
            {loading && <li>Loading...</li>}
            {error   && <li>{error}</li>}

            {!loading && filteredQuotes.length === 0 && (
              <li>No quotes found.</li>
            )}

            {filteredQuotes.map(q => (
              <li
                key={q.id}
                className={selectedId === q.id ? "active" : ""}
                onClick={() => setSelectedId(q.id)}
              >
                <p>{q.fullName}</p>
                <div
                  className="status-icon"
                  style={{ background: STATUS_CONFIG[q.status].color }}
                  title={STATUS_CONFIG[q.status].label}
                />
              </li>
            ))}
          </ul>

        </div>

        {/* ── RIGHT: quote details ── */}
        <div className="details-container">

          {/* No quote selected yet */}
          {!selectedQuote && (
            <p className="subtitle">Select a user to view their quote.</p>
          )}

          {/* Quote details */}
          {selectedQuote && (
            <>
              <div className="user-details">
                <h2>{selectedQuote.fullName}</h2>
                <p className="subtitle">{selectedQuote.email}</p>
                <p className="subtitle">{selectedQuote.phoneNum}</p>
                <p className="subtitle">{selectedQuote.compName}</p>
              </div>

              {/* Status badge */}
              <div className="check-status">
                <div
                  className="status-icon"
                  style={{ background: STATUS_CONFIG[selectedQuote.status].color }}
                />
                <p>{STATUS_CONFIG[selectedQuote.status].label}</p>
              </div>

              <div className="quote-section">

                {/* License Type */}
                <div className="quote-field">
                  <p className="quote-label">License Type</p>
                  <span className="quote-value">{selectedQuote.licenseType}</span>
                </div>

                {/* Project Details */}
                <div className="quote-field">
                  <p className="quote-label">Message / Description</p>
                  <textarea
                    className="quote-textarea"
                    readOnly
                    value={selectedQuote.projectDetails}
                  />
                </div>

                {/* Documents */}
                <div className="quote-field">
                  <p className="quote-label">Uploaded Documents</p>
                  <div className="display-files">
                    {selectedQuote.documentFiles.length === 0 && (
                      <p className="subtitle">No documents uploaded.</p>
                    )}
                    {selectedQuote.documentFiles.map((doc, i) => (
                      <a
                        key={i}
                        href={doc.url}
                        target="_blank"
                        rel="noreferrer"
                        className="file-link"
                      >
                        📄 {doc.name}
                      </a>
                    ))}
                  </div>
                </div>

              </div>

              {/* Action buttons */}
              <div className="btn-section">
                <button onClick={() => updateStatus(selectedQuote.id, "accepted")}>
                  <img src={tickIcon} alt="Check Icon" />
                  Mark as Accepted
                </button>
                <button onClick={() => updateStatus(selectedQuote.id, "sent")}>
                  <img src={requestQuoteIcon} alt="Quote Icon" />
                  Send Quote
                </button>
                <button onClick={() => updateStatus(selectedQuote.id, "rejected")}>
                  <img src={rejectIcon} alt="Cancel Icon" />
                  Reject Quotation
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
